import React, { useState, useEffect, useCallback } from 'react';
import { Plus, CheckCircle, XCircle, Loader2, MapPin, Clock, Building2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { billingAddressAPI } from '../../services/api';
import { toast } from 'sonner';

const STATES = ['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Delhi','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal'];

const statusColors = { Pending: 'bg-amber-100 text-amber-700', Approved: 'bg-emerald-100 text-emerald-700', Rejected: 'bg-red-100 text-red-700' };
const typeColors = { Branch: 'bg-blue-100 text-blue-700', Site: 'bg-orange-100 text-orange-700', Vendor: 'bg-purple-100 text-purple-700', Custom: 'bg-gray-100 text-gray-700' };

const BillingAddressModule = () => {
  const user = JSON.parse(localStorage.getItem('prismoline_user') || '{}');
  const isAccountsMgr = user.email === 'accounts@prismoline.com' || user.role === 'Admin';

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showApprove, setShowApprove] = useState(false);
  const [selectedReq, setSelectedReq] = useState(null);
  const [filter, setFilter] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    name: '', address: '', city: '', state: '', pincode: '', gstin: '',
    contact_person: '', phone: '', email: '', suggested_type: 'Branch', reason: '',
  });

  const [approveType, setApproveType] = useState('Branch');
  const [rejectReason, setRejectReason] = useState('');

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const data = await billingAddressAPI.listRequests(filter ? { status: filter } : {});
      setRequests(data);
    } catch { toast.error('Failed to load requests'); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.address.trim()) { toast.error('Name and address are required'); return; }
    setSubmitting(true);
    try {
      await billingAddressAPI.createRequest(form);
      toast.success('Billing address request submitted for approval');
      setShowAdd(false);
      setForm({ name: '', address: '', city: '', state: '', pincode: '', gstin: '', contact_person: '', phone: '', email: '', suggested_type: 'Branch', reason: '' });
      fetchRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
    setSubmitting(false);
  };

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await billingAddressAPI.approve(selectedReq.id, { approved_type: approveType });
      toast.success(`Approved as ${approveType}`);
      setShowApprove(false);
      fetchRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to approve'); }
    setSubmitting(false);
  };

  const handleReject = async () => {
    setSubmitting(true);
    try {
      await billingAddressAPI.reject(selectedReq.id, { reason: rejectReason });
      toast.success('Request rejected');
      setShowApprove(false);
      fetchRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reject'); }
    setSubmitting(false);
  };

  const openApproveDialog = (req) => {
    setSelectedReq(req);
    setApproveType(req.suggested_type || 'Branch');
    setRejectReason('');
    setShowApprove(true);
  };

  const pendingCount = requests.filter(r => r.status === 'Pending').length;

  return (
    <div className="space-y-4" data-testid="billing-address-module">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-bold text-gray-900">Billing Addresses</h2>
          <p className="text-[10px] text-gray-400 mt-0.5">Request new addresses — approved by Accounts Manager</p>
        </div>
        <Button size="sm" onClick={() => setShowAdd(true)} data-testid="request-billing-btn" className="gap-1.5 bg-[#24549c] hover:bg-[#1a3f7a]">
          <Plus className="w-3.5 h-3.5" /> Request New Address
        </Button>
      </div>

      <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5 w-fit">
        {['', 'Pending', 'Approved', 'Rejected'].map(s => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`ba-filter-${s || 'all'}`}
            className={`px-2.5 py-1 text-[10px] font-semibold rounded-md transition-colors ${filter === s ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
            {s || 'All'} {s === 'Pending' && pendingCount > 0 ? `(${pendingCount})` : ''}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
      ) : requests.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm">No billing address requests</div>
      ) : (
        <div className="space-y-2">
          {requests.map(req => (
            <div key={req.id} className="bg-white rounded-xl border border-gray-100 p-4 flex items-start justify-between gap-4 hover:shadow-sm transition-shadow" data-testid={`ba-request-${req.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <MapPin className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-sm font-semibold text-gray-900">{req.name}</span>
                  <Badge className={`text-[9px] ${statusColors[req.status]}`}>{req.status}</Badge>
                  <Badge className={`text-[9px] ${typeColors[req.approved_type || req.suggested_type]}`}>{req.approved_type || req.suggested_type}</Badge>
                </div>
                <p className="text-xs text-gray-500 ml-5.5">{req.address}{req.city ? `, ${req.city}` : ''}{req.state ? `, ${req.state}` : ''} {req.pincode}</p>
                {req.gstin && <p className="text-[10px] text-gray-400 ml-5.5 font-mono mt-0.5">GSTIN: {req.gstin}</p>}
                <div className="flex items-center gap-3 mt-1.5 ml-5.5 text-[10px] text-gray-400">
                  <span>By: {req.requester_name}</span>
                  <span>{new Date(req.created_at).toLocaleDateString('en-IN')}</span>
                  {req.reason && <span>Reason: {req.reason}</span>}
                </div>
                {req.status === 'Approved' && <p className="text-[10px] text-emerald-600 mt-1 ml-5.5">Approved by {req.approved_by_name} as {req.approved_type}</p>}
                {req.status === 'Rejected' && <p className="text-[10px] text-red-500 mt-1 ml-5.5">Rejected: {req.rejection_reason || 'No reason'}</p>}
              </div>
              {isAccountsMgr && req.status === 'Pending' && (
                <Button size="sm" variant="outline" onClick={() => openApproveDialog(req)} data-testid={`ba-review-${req.id}`} className="gap-1.5 text-xs flex-shrink-0">
                  <Clock className="w-3 h-3" /> Review
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Request New Address Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-lg" data-testid="request-billing-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><MapPin className="w-4 h-4" style={{ color: '#24549c' }} /> Request New Billing Address</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Name / Title <span className="text-red-500">*</span></label>
              <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="e.g. Jaipur Godown" data-testid="ba-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Full Address <span className="text-red-500">*</span></label>
              <Textarea rows={2} value={form.address} onChange={e => setForm(f => ({...f, address: e.target.value}))} placeholder="Street, locality..." data-testid="ba-address" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">City</label>
                <Input value={form.city} onChange={e => setForm(f => ({...f, city: e.target.value}))} placeholder="Jaipur" data-testid="ba-city" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">State</label>
                <Select value={form.state} onValueChange={v => setForm(f => ({...f, state: v}))}>
                  <SelectTrigger data-testid="ba-state"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Pincode</label>
                <Input value={form.pincode} onChange={e => setForm(f => ({...f, pincode: e.target.value}))} placeholder="302001" data-testid="ba-pincode" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">GSTIN</label>
                <Input value={form.gstin} onChange={e => setForm(f => ({...f, gstin: e.target.value.toUpperCase()}))} placeholder="22AAAAA0000A1Z5" className="font-mono" data-testid="ba-gstin" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Suggested Type</label>
                <Select value={form.suggested_type} onValueChange={v => setForm(f => ({...f, suggested_type: v}))}>
                  <SelectTrigger data-testid="ba-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Branch">Branch (Godown/Office)</SelectItem>
                    <SelectItem value="Site">Site (Temporary)</SelectItem>
                    <SelectItem value="Vendor">Vendor</SelectItem>
                    <SelectItem value="Custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Contact Person</label>
                <Input value={form.contact_person} onChange={e => setForm(f => ({...f, contact_person: e.target.value}))} data-testid="ba-contact" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Phone</label>
                <Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} data-testid="ba-phone" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Email</label>
                <Input value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} data-testid="ba-email" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Reason for Request</label>
              <Textarea rows={2} value={form.reason} onChange={e => setForm(f => ({...f, reason: e.target.value}))} placeholder="Why is this address needed?" data-testid="ba-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} data-testid="ba-submit" className="bg-[#24549c] hover:bg-[#1a3f7a] gap-1.5">
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve/Reject Dialog */}
      <Dialog open={showApprove} onOpenChange={setShowApprove}>
        <DialogContent data-testid="approve-billing-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Building2 className="w-4 h-4" style={{ color: '#24549c' }} /> Review — {selectedReq?.name}</DialogTitle></DialogHeader>
          {selectedReq && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg text-xs space-y-1">
                <p><span className="text-gray-500">Address:</span> {selectedReq.address}</p>
                <p><span className="text-gray-500">City/State:</span> {selectedReq.city}, {selectedReq.state} {selectedReq.pincode}</p>
                {selectedReq.gstin && <p><span className="text-gray-500">GSTIN:</span> <span className="font-mono">{selectedReq.gstin}</span></p>}
                {selectedReq.contact_person && <p><span className="text-gray-500">Contact:</span> {selectedReq.contact_person} {selectedReq.phone}</p>}
                <p><span className="text-gray-500">Requested by:</span> {selectedReq.requester_name}</p>
                {selectedReq.reason && <p><span className="text-gray-500">Reason:</span> {selectedReq.reason}</p>}
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Approve as Type</label>
                <Select value={approveType} onValueChange={setApproveType}>
                  <SelectTrigger data-testid="approve-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Branch">Branch (permanent godown/office)</SelectItem>
                    <SelectItem value="Site">Site (temporary project address)</SelectItem>
                    <SelectItem value="Vendor">Vendor (create as vendor)</SelectItem>
                    <SelectItem value="Custom">Custom (billing address only)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Rejection Reason (if rejecting)</label>
                <Textarea rows={2} value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Reason for rejection..." data-testid="reject-reason" />
              </div>
            </div>
          )}
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowApprove(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} disabled={submitting} data-testid="reject-billing-btn" className="gap-1.5">
              {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />} Reject
            </Button>
            <Button onClick={handleApprove} disabled={submitting} data-testid="approve-billing-btn" className="bg-emerald-600 hover:bg-emerald-700 gap-1.5">
              {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />} Approve as {approveType}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillingAddressModule;
